import type { WalletGroupedAccounts } from '@polaris/core/models'
import { motion } from 'motion/react'
import { type FormEvent, useCallback, useMemo } from 'react'
import { errorToast } from '@/src/components/ui/toast'
import { usePortfolioView } from '@/src/stores/portfolio-drawer/actions'
import { useEditWalletName } from '@/src/stores/user/actions'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'

export const EditWalletName = () => {
  const { currentView, goBack } = usePortfolioView()
  const { mutateAsync, isPending, isSuccess, isError } = useEditWalletName()

  const { account } = currentView.metadata as {
    account: WalletGroupedAccounts
  }

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const formData = new FormData(event.currentTarget)

      const walletName = formData.get('walletName') as string | null

      if (walletName) {
        const editedAccount = {
          account,
          name: walletName,
        }

        try {
          await mutateAsync(editedAccount)

          // Navigate back to show updated name
          // The mutation will update accountsAtom (guest) or refetch (logged-in)
          // which will automatically update walletAccountsAtom
          // and ManageWallet will show the new name
          goBack()
        } catch (error) {
          console.error('Failed to update wallet name:', error)
          errorToast(
            error instanceof Error
              ? error.message
              : 'Failed to update wallet name. Please try again.',
          )
        }
      }
    },
    [mutateAsync, account, goBack],
  )

  const buttonText = useMemo(() => {
    if (isPending) {
      return 'Saving...'
    }

    if (isSuccess) {
      return 'Successfully saved'
    }

    if (isError) {
      return 'Something went wrong, try again.'
    }

    return 'Save changes'
  }, [isPending, isSuccess, isError])

  return (
    <motion.form
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col items-center pb-10'
      onSubmit={onSubmit}
      method='POST'
    >
      <p className='text-secondary text-center mb-8'>
        Choose a wallet name to help you easily identify it in your portfolio.
      </p>

      <Input
        name='walletName'
        className='mb-4'
        dimension='medium'
        defaultValue={account.name}
        required
        minLength={1}
      />

      <Button
        type='submit'
        className='w-full'
        variant='primary'
        disabled={isPending || isSuccess}
      >
        {buttonText}
      </Button>
    </motion.form>
  )
}
